import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTimes, faImage } from '@fortawesome/free-solid-svg-icons';
import { addArticle, getCategories } from '../../services/firestoreService';
import { uploadImage } from '../../utils/uploadImage';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import PageHead from '../../components/common/PageHead';
import Alert from '../../components/common/Alert';

const AddArticle = () => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    metaDescription: '',
    featuredImage: null,
    category: '',
    ctaText: '',
    ctaHeading: '',
    ctaButtonText: '',
    ctaButtonLink: '',
    dateCreated: new Date(),
  });

  const [imageName, setImageName] = useState('');
  const [categories, setCategories] = useState([]);

  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const [editorKey, setEditorKey] = useState(Date.now());

  useEffect(() => {
    const fetchCategories = async () => {
      const fetched = await getCategories();
      setCategories(fetched);
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 4000);
      return () => clearTimeout(timer);
    }
    if (error) {
      const timer = setTimeout(() => setError(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'featuredImage') {
      setFormData({ ...formData, featuredImage: files[0] });
      setImageName(files[0]?.name || '');
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, featuredImage: null });
    setImageName('');
  };

  const handleEditorChange = (content) => {
    setFormData((prev) => ({ ...prev, content }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsUploading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (!formData.content.trim() || formData.content === '<p><br></p>') {
      setIsUploading(false);
      setError('Content cannot be empty.');
      return;
    }

    try {
      let imageUrl = '';
      if (formData.featuredImage) {
        imageUrl = await uploadImage(formData.featuredImage, 'article/featured_images/');
      }

      const ctaData =
        formData.ctaText || formData.ctaHeading || formData.ctaButtonText || formData.ctaButtonLink
          ? {
              ctaText: formData.ctaText,
              ctaHeading: formData.ctaHeading,
              ctaButtonText: formData.ctaButtonText,
              ctaButtonLink: formData.ctaButtonLink,
            }
          : {};

      const articleData = {
        title: formData.title,
        content: formData.content,
        metaDescription: formData.metaDescription,
        category: formData.category,
        featuredImage: imageUrl,
        dateCreated: new Date(),
        ...ctaData,
      };

      await addArticle(articleData);

      setSuccessMessage('Article submitted successfully!');
      setFormData({
        title: '',
        content: '',
        metaDescription: '',
        featuredImage: null,
        category: '',
        ctaText: '',
        ctaHeading: '',
        ctaButtonText: '',
        ctaButtonLink: '',
        dateCreated: new Date(),
      });
      setImageName('');
      setEditorKey(Date.now());
    } catch (err) {
      console.error(err);
      setError('Failed to submit the article. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className='max-w-screen overflow-hidden'>
      <PageHead name='Add New Article' />
      <div className='flex flex-col items-center justify-center my-16 mx-6'>

        <div className="w-full md:w-2/3">
          {error && <Alert type="error" message={error} />}
          {isUploading && <Alert type="info" message="Submitting article, please wait..." />}
          {successMessage && <Alert type="success" message={successMessage} />}
        </div>

        <form
          className="flex flex-col justify-center space-y-8 text-grey-600 w-full md:w-2/3"
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          <div>
            <label className="text-xs">Title</label>
            <input
              type="text"
              name="title"
              placeholder="Article Title"
              onChange={handleChange}
              value={formData.title}
              required
              className="w-full p-3 bg-transparent border border-gray-600 border-2 rounded-xs focus:outline-none placeholder-gray-500"
              disabled={isUploading}
            />
          </div>

          <div>
            <div className='flex justify-between items-center'>
              <label className='text-xs'>Meta Description</label>
              <div className="text-xs text-gray-500">
                {formData.metaDescription.length}/160 characters
              </div>
            </div>
            <textarea
              name="metaDescription"
              placeholder="Meta Description"
              onChange={handleChange}
              maxLength="160"
              value={formData.metaDescription}
              required
              className="w-full p-3 border bg-transparent border-gray-600 border-2 rounded-xs h-24 focus:outline-none placeholder-gray-500"
              disabled={isUploading}
            />
          </div>

          <div>
            <label className="text-xs">Category</label>
            <select
              name="category"
              onChange={handleChange}
              value={formData.category}
              required
              className="w-full p-3 bg-transparent border border-gray-600 border-2 rounded-xs focus:outline-none text-gray-500"
              disabled={isUploading}
            >
              <option value="" disabled>Select Category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs">Featured Image</label>
            <label className="block p-3 bg-transparent border border-gray-600 border-2 rounded-xs text-gray-500 cursor-pointer">
              {imageName ? (
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faImage} className='text-lg mr-3' />
                  <span className="truncate">{imageName}</span>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="ml-2 text-red-500 hover:text-red-700"
                    disabled={isUploading}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUpload} className='text-lg mr-3' />
                  <span className="placeholder-gray-500">Upload Featured Image</span>
                </>
              )}
              <input
                type="file"
                name="featuredImage"
                accept="image/*"
                onChange={handleChange}
                className="sr-only"
                required
                disabled={isUploading}
              />
            </label>
            {formData.featuredImage && typeof formData.featuredImage !== 'string' && (
              <img
                src={URL.createObjectURL(formData.featuredImage)}
                alt="Preview"
                className="h-20 mt-2"
              />
            )}
          </div>

          {['ctaHeading', 'ctaText', 'ctaButtonText', 'ctaButtonLink'].map((field) => (
            <div key={field}>
              <label className="text-xs">{field.replace(/cta/, 'CTA ').replace(/([A-Z])/g, ' $1')} (Optional)</label>
              <input
                type="text"
                name={field}
                placeholder={`Enter ${field}`}
                value={formData[field]}
                onChange={handleChange}
                className="w-full p-3 bg-transparent border border-gray-600 border-2 rounded-xs focus:outline-none placeholder-gray-500"
                disabled={isUploading}
              />
            </div>
          ))}

          <div className='mb-10 overflow-hidden'>
            <label className='text-xs'>Content</label>
            <ReactQuill
              key={editorKey}
              value={formData.content}
              theme='snow'
              required
              onChange={handleEditorChange}
              className="border border-gray-600 border-2 rounded-xs my-quill-editor"
              style={{ height: '100%', overflowY: 'auto' }}
              readOnly={isUploading}
            />
          </div>

          <div className='my-32 flex justify-center items-center'>
            <button
              type="submit"
              className='bg-primary text-white text-lg px-5 py-2 hover:bg-blue-600 rounded disabled:opacity-60'
              disabled={isUploading}
            >
              {isUploading ? 'Submitting...' : 'Submit Article'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddArticle;
